import React, { useCallback, useEffect, useState } from 'react';
import { Cloud, Play, PauseCircle, UserX, Wallet, ShieldCheck } from 'lucide-react';
import { StatCard } from '../../components/shared/StatCard';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatCount, formatPercent } from '../../lib/formatNumber';
import cloudAssetService, {
  CloudOverview,
} from '../../services/cloudAssetService';

const BreakdownBars: React.FC<{
  title: string;
  rows: { label: string; count: number }[];
}> = ({ title, rows }) => {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-sm font-semibold">{title}</h3>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
        ) : (
          rows.map((row) => (
            <div key={row.label} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="truncate">{row.label}</span>
                <span className="text-muted-foreground">
                  {formatCount(row.count)}
                </span>
              </div>
              <Progress value={(row.count / max) * 100} className="h-1.5" />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Cloud portfolio overview (User Story 90).
 *
 * Every number is scoped server-side to the caller's tier, so a headline here
 * never exceeds what the Resources tab will actually show them.
 */
export const CloudOverviewTab: React.FC = () => {
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const [overview, setOverview] = useState<CloudOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await cloudAssetService.getOverview());
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to load cloud overview', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = overview?.kpis;
  const costAsOf = kpis?.costAsOf
    ? new Date(kpis.costAsOf).toLocaleDateString()
    : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Resources"
          value={formatCount(kpis?.totalResources ?? 0)}
          subtitle={`${formatCount(kpis?.cloudEnvironments ?? 0)} environment(s)`}
          icon={Cloud}
          iconColor="bg-indigo-500/10 text-indigo-500"
          loading={loading}
        />
        <StatCard
          title="Running"
          value={formatCount(kpis?.running ?? 0)}
          subtitle="provider state"
          icon={Play}
          iconColor="bg-emerald-500/10 text-emerald-500"
          loading={loading}
        />
        <StatCard
          title="Stopped"
          value={formatCount(kpis?.stoppedOrTerminated ?? 0)}
          subtitle="stopped or terminated"
          icon={PauseCircle}
          iconColor="bg-amber-500/10 text-amber-500"
          loading={loading}
        />
        <StatCard
          title="Unassigned"
          value={formatCount(kpis?.unassigned ?? 0)}
          subtitle="no department or owner"
          icon={UserX}
          iconColor="bg-rose-500/10 text-rose-500"
          loading={loading}
        />
        <StatCard
          title="Monthly Spend"
          value={formatCost(kpis?.monthlyCost ?? 0, kpis?.baseCurrency)}
          // The as-of date rides on the card itself, so the number is never
          // read as a live figure.
          subtitle={costAsOf ? `as entered, oldest ${costAsOf}` : 'no cost recorded'}
          icon={Wallet}
          iconColor="bg-sky-500/10 text-sky-500"
          loading={loading}
        />
        <StatCard
          title="Compliance"
          value={formatPercent(kpis?.compliancePercent ?? 0)}
          subtitle="records with no audit findings"
          icon={ShieldCheck}
          iconColor="bg-violet-500/10 text-violet-500"
          loading={loading}
        />
      </div>

      {(kpis?.unconvertibleCurrencies?.length ?? 0) > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          Spend excludes resources billed in{' '}
          {kpis!.unconvertibleCurrencies.join(', ')} — no exchange rate is
          configured for those currencies.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <BreakdownBars title="By Provider" rows={overview?.byProvider ?? []} />
        <BreakdownBars
          title="By Resource Type"
          rows={overview?.byResourceType ?? []}
        />
        <BreakdownBars
          title="By Department"
          rows={overview?.byDepartment ?? []}
        />
        <BreakdownBars
          title="By Lifecycle Status"
          rows={overview?.byLifecycleStatus ?? []}
        />
      </div>
    </div>
  );
};

export default CloudOverviewTab;
